import type { ItemVariantAxisOptions } from '../api/masters';
import type { PurchaseOrder, SavePurchaseOrderInput } from '../api/purchaseOrders';
import type { PurchaseOrderStatus } from '../generated/enums';
import { emptyLine, type IntakeDraft } from '../units/intake';
import {
  receiveLine,
  splitAxes,
  toIntakeDraft,
  type ReceiveDraft,
  type ReceiveLine,
} from '../units/receive';
import type { StatusTone } from '../vocabulary';

/**
 * The purchase order screen's rules (spec §16, step 2 of
 * `docs/Purchase_Sale_Outstanding_Design.md`) — the order's lines, what stops it being saved, the
 * request it sends, and the delivery a Receive starts from. Pure, so the website and the phone ask
 * the same questions and get the same answers.
 */

/** One configuration on the order — one size of one design and look. */
export interface OrderLineDraft {
  /** Stable across removals, unlike an index. Never sent. */
  key: string;
  itemId: number | null;
  /** attributeId → valueId, exactly the axes the chosen design declares. */
  axisValues: Record<number, number>;
  quantity: number;
  /** Per garment, before tax. Null when no price was agreed. */
  rate: number | null;
}

export interface OrderDraft {
  supplierVendorId: number | null;
  /** `YYYY-MM-DD`. */
  orderDate: string;
  /** `YYYY-MM-DD`, or empty for none. */
  expectedOn: string;
  notes: string;
  lines: OrderLineDraft[];
}

/** The server's own bounds, so the screen refuses what it would. */
export const maxOrderLines = 50;
const maxOrderQuantity = 1000;

let sequence = 0;

function orderLine(): OrderLineDraft {
  sequence += 1;

  return { key: `order-${sequence}`, itemId: null, axisValues: {}, quantity: 1, rate: null };
}

/** Garments and value over the lines that name a design — what the summary states. */
export function orderTotals(draft: OrderDraft): { units: number; value: number | null } {
  const named = draft.lines.filter((line) => line.itemId !== null);
  const priced = named.filter((line) => line.rate !== null);

  return {
    units: named.reduce((sum, line) => sum + line.quantity, 0),
    value:
      priced.length === 0
        ? null
        : priced.reduce((sum, line) => sum + line.quantity * line.rate!, 0),
  };
}

const configurationKey = (line: OrderLineDraft) =>
  `${line.itemId}|${Object.entries(line.axisValues)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([attribute, value]) => `${attribute}:${value}`)
    .join(',')}`;

/**
 * Why the order cannot be saved yet, as sentences for the screen. The server refuses each of these
 * too; saying them first saves a round trip on a page that can hold fifty lines.
 */
export function orderProblems(
  draft: OrderDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
): string[] {
  const problems: string[] = [];

  if (draft.supplierVendorId === null) {
    problems.push('Choose the supplier.');
  }

  if (draft.expectedOn && draft.expectedOn < draft.orderDate) {
    problems.push('The expected date cannot be before the order date.');
  }

  const named = draft.lines.filter((line) => line.itemId !== null);

  if (named.length === 0) {
    problems.push('Name a design on the first line.');
  } else if (named.length > maxOrderLines) {
    problems.push(`An order holds at most ${maxOrderLines} lines.`);
  }

  draft.lines.forEach((line, index) => {
    if (line.itemId === null) {
      return;
    }

    const axes = axesByItem[line.itemId];
    const where = `Line ${index + 1}:`;

    if (axes === undefined) {
      problems.push(`${where} loading this design’s options…`);
    } else if (axes.length === 0) {
      problems.push(`${where} this design’s category has no sizes or colours to order by.`);
    } else {
      const missing = axes.filter((axis) => line.axisValues[axis.attributeId] === undefined);

      if (missing.length > 0) {
        problems.push(
          `${where} choose ${missing.map((axis) => axis.attributeName.toLowerCase()).join(' and ')}.`,
        );
      }
    }

    if (!Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > maxOrderQuantity) {
      problems.push(`${where} order between 1 and ${maxOrderQuantity}.`);
    }

    if (line.rate !== null && line.rate < 0) {
      problems.push(`${where} a rate cannot be negative.`);
    }
  });

  const seen = new Set<string>();

  draft.lines.forEach((line, index) => {
    if (line.itemId === null) {
      return;
    }

    const key = configurationKey(line);

    if (seen.has(key)) {
      problems.push(`Line ${index + 1}: the same design and size is on the order twice.`);
    }

    seen.add(key);
  });

  return problems;
}

/** The request — blank lines dropped, axes as pairs, empty strings as nulls. */
export function toOrderRequest(draft: OrderDraft): SavePurchaseOrderInput {
  return {
    supplierVendorId: draft.supplierVendorId ?? 0,
    orderDate: draft.orderDate,
    expectedOn: draft.expectedOn || null,
    notes: draft.notes.trim() || null,
    lines: draft.lines
      .filter((line): line is OrderLineDraft & { itemId: number } => line.itemId !== null)
      .map((line) => ({
        itemId: line.itemId,
        axisValues: Object.entries(line.axisValues).map(([attributeId, valueId]) => ({
          attributeId: Number(attributeId),
          valueId,
        })),
        quantity: line.quantity,
        rate: line.rate,
      })),
  };
}

/**
 * The delivery a Receive starts from: every line still to come, at its pending quantity and the
 * order's rate as the cost, from the order's supplier. Book in stock locks each line's design and
 * size to the order's; the storeman changes only how many came and what they cost.
 */
export function receivingDraft(order: PurchaseOrder, today: string): IntakeDraft {
  return {
    purchasedOn: today,
    supplierVendorId: order.supplierVendorId,
    supplierReference: '',
    notes: '',
    lines: order.lines
      .filter((line) => line.pendingQuantity > 0)
      .map((line) => ({
        ...emptyLine(),
        itemId: line.itemId,
        axisValues: Object.fromEntries(line.axisValues.map((a) => [a.attributeId, a.valueId])),
        quantity: line.pendingQuantity,
        purchaseCost: line.rate,
        purchaseOrderLineId: line.id,
      })),
  };
}

/** Whether a Receive has anything to start from. */
export function canReceive(order: PurchaseOrder): boolean {
  return order.lines.some((line) => line.pendingQuantity > 0);
}

/** Cancelling is for an order nothing has come against; closing short for one part-received. */
export function canCancel(order: PurchaseOrder): boolean {
  return order.status === 'Open';
}

export function canCloseShort(order: PurchaseOrder): boolean {
  return order.status === 'PartlyReceived';
}

export const orderStatusLabel: Record<PurchaseOrderStatus, string> = {
  Open: 'Open',
  PartlyReceived: 'Part received',
  Received: 'Received',
  Cancelled: 'Cancelled',
  ClosedShort: 'Closed short',
};

export const orderStatusTone: Record<PurchaseOrderStatus, StatusTone> = {
  Open: 'brand',
  PartlyReceived: 'warning',
  Received: 'positive',
  Cancelled: 'neutral',
  ClosedShort: 'neutral',
};

/**
 * An order counted the way the design canvas asks (2026-10-22) — a design, its look, how many of
 * each size and one rate — as the order's lines: one per size counted, the look and size together as
 * its axes. The grid is the phone Receive's own (`units/receive.ts`), so ordering and receiving
 * count a design the same way.
 */
export function orderFromCounts(
  base: Omit<OrderDraft, 'lines'>,
  counted: ReceiveDraft,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
): OrderDraft {
  return {
    ...base,
    lines: toIntakeDraft(counted, axesByItem).lines.map((line) => ({
      ...orderLine(),
      itemId: line.itemId,
      axisValues: line.axisValues,
      quantity: line.quantity,
      rate: line.purchaseCost,
    })),
  };
}

/**
 * An order back into the grid it was counted on, for Change while nothing has been received — one
 * row per design, look and rate, with a count for each size. One design and look at two rates stays
 * two rows, so nothing the order said is lost when it is saved again.
 */
export function countsFromOrder(
  order: PurchaseOrder,
  axesByItem: Record<number, ItemVariantAxisOptions[] | undefined>,
): ReceiveDraft {
  const rows = new Map<string, ReceiveLine>();

  for (const line of order.lines) {
    const values: Record<number, number> = Object.fromEntries(
      line.axisValues.map((axis) => [axis.attributeId, axis.valueId]),
    );
    const { looks, sizes } = splitAxes(axesByItem[line.itemId] ?? []);
    const look = Object.fromEntries(
      looks
        .filter((axis) => values[axis.attributeId] !== undefined)
        .map((axis) => [axis.attributeId, values[axis.attributeId]!]),
    );
    // The key `sizeChoices` gives the same size.
    const size = sizes.map((axis) => `${axis.attributeId}:${values[axis.attributeId]}`).join('/');
    const rowKey = `${line.itemId}|${JSON.stringify(look)}|${line.rate}`;
    const row = rows.get(rowKey) ?? { ...receiveLine(line.itemId), look, purchaseCost: line.rate };

    rows.set(rowKey, {
      ...row,
      counts: { ...row.counts, [size]: (row.counts[size] ?? 0) + line.quantity },
    });
  }

  return {
    receivedOn: order.orderDate,
    supplierVendorId: order.supplierVendorId,
    note: '',
    lines: [...rows.values()],
  };
}
